import React, { useRef } from "react";
import { Box, Typography, IconButton, Paper, styled } from "@mui/material";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import ArrowForwardIcon from "@mui/icons-material/ArrowForward";
import { ScenarioCard } from "@/services/dashboard/sm-api";

const StyledCard = styled(Paper)(() => ({
  padding: "24px",
  borderRadius: "12px",
  cursor: "pointer",
  transition: "all 0.2s ease",
  border: "1px solid #E6E7E7",
  minWidth: "420px",
  maxWidth: "420px",
  "&:hover": {
    backgroundColor: "#F9FAFB",
    transform: "translateY(-2px)",
    boxShadow: "0px 4px 8px rgba(0, 0, 0, 0.05)",
  },
}));

const ScrollButton = styled(IconButton)(() => ({
  position: "absolute",
  top: "50%",
  transform: "translateY(-50%)",
  backgroundColor: "white",
  boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
  "&:hover": {
    backgroundColor: "#F9FAFB",
  },
  zIndex: 1,
}));

interface ScenarioListProps {
  scenarios: ScenarioCard[];
  selectedScenarioId: string | null;
  onScenarioSelect: (scenarioId: string) => void;
}

const ScenarioList: React.FC<ScenarioListProps> = ({
  scenarios,
  selectedScenarioId,
  onScenarioSelect,
}) => {
  const scrollContainerRef = useRef<HTMLDivElement>(null);

  const scroll = (direction: "left" | "right") => {
    if (scrollContainerRef.current) {
      const scrollAmount = 320; // card width + gap
      scrollContainerRef.current.scrollBy({
        left: direction === "left" ? -scrollAmount : scrollAmount,
        behavior: "smooth",
      });
    }
  };

  // Reverse the scenarios array to show newer ones on the left
  const sortedScenarios = [...scenarios].reverse();

  return (
    <Box sx={{ position: "relative" }}>
      <ScrollButton onClick={() => scroll("left")} sx={{ left: -20 }}>
        <ChevronLeftIcon />
      </ScrollButton>

      <Box
        ref={scrollContainerRef}
        sx={{
          display: "flex",
          gap: 2,
          overflowX: "auto",
          px: 1,
          py: 1,
          scrollbarWidth: "none",
          "&::-webkit-scrollbar": { display: "none" },
          scrollBehavior: "smooth",
        }}
      >
        {sortedScenarios.map((scenario) => (
          <StyledCard
            key={scenario._id}
            onClick={() => onScenarioSelect(scenario._id)}
            sx={{
              border: selectedScenarioId === scenario._id ? "2px solid #0B494B" : undefined,
            }}
          >
            <Box
              sx={{
                display: "flex",
                justifyContent: "space-between",
                alignItems: "flex-start",
                mb: 1,
              }}
            >
              <Box sx={{ flex: 1 }}>
                <Box sx={{ display: "flex", gap: 1, alignItems: "center", mb: 1.5 }}>
                  <Typography variant="h5" sx={{ color: "#0B494B", fontSize: "1.5rem" }}>
                    {scenario.country_list.length > 1 ? "🌎" : "📈"}
                  </Typography>
                </Box>
                <Typography
                  variant="h6"
                  sx={{ fontWeight: 600, color: "#101828", mb: 1, fontSize: "1.3rem" }}
                >
                  {scenario.name}
                </Typography>
                <Typography
                  sx={{
                    color: "#475467",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    overflow: "hidden",
                    lineHeight: "1.5",
                    height: "auto",
                    minHeight: "60px",
                    fontSize: "1.1rem"
                  }}
                >
                  Rate: {scenario.rate}%
                  <br />
                  Countries: {scenario.country_list.join(", ")}
                </Typography>
              </Box>
              <ArrowForwardIcon sx={{ color: "#0B494B", fontSize: 24, ml: 1.5 }} />
            </Box>
          </StyledCard>
        ))}
      </Box>

      <ScrollButton onClick={() => scroll("right")} sx={{ right: -20 }}>
        <ChevronRightIcon />
      </ScrollButton>
    </Box>
  );
};

export default ScenarioList; 